import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsBox, BsPlus, BsTrash, BsSearch, BsCollection, BsChevronRight, BsStickyFill } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import Table from '#globalComponents/Table/Table';
import { CELL_TEXT_MUTED } from '#globalComponents/Table/tableStyles';
import Tabs from '#globalComponents/Tabs/Tabs';
import { useErrorStore } from '#lib/stores/errorStore';
import { truncateName } from '#lib/textUtils';

import CustomFieldsSection from '../../components/common/CustomFieldsSection/CustomFieldsSection';
import { useVisibleFieldDefs } from '../../models/customFields/queries';
import { useCreateProduct, useCreateBundle } from '../../models/products/mutations';
import { useProductsList, useBundlesList } from '../../models/products/queries';
import { buildDefaultFieldPatch } from '../../utils/fieldValues';
import { formatPrice } from '../../utils/formatters';

const PRODUCT_COLUMNS = [
	{
		key: 'articleNumber',
		label: 'Artikelnr.',
		render: (p) => <span className="font-mono text-bgBase-800">{p.articleNumber || '—'}</span>,
	},
	{
		key: 'name',
		label: 'Produkt',
		render: (p) => (
			<>
				<div className="font-medium text-text" title={p.name}>
					{truncateName(p.name)}
				</div>
				{p.description && <div className={`truncate max-w-xs mt-0.5 ${CELL_TEXT_MUTED}`}>{p.description}</div>}
			</>
		),
	},
	{
		key: 'price',
		label: 'Preis',
		align: 'right',
		render: (p) => <span className="font-bold tabular-nums">{formatPrice(p.price)}</span>,
	},
	{
		key: 'notes',
		label: 'Notizen',
		render: (p) =>
			p.notes ? (
				<span className="inline-flex items-center gap-1 text-caution-700" title={p.notes}>
					<BsStickyFill className="w-3 h-3" /> Notiz
				</span>
			) : (
				<span className="text-bgBase-800">—</span>
			),
	},
	{
		key: 'chevron',
		align: 'right',
		width: 32,
		render: () => <BsChevronRight className="w-3.5 h-3.5 text-bgBase-800" />,
	},
];

const BUNDLE_COLUMNS = [
	{
		key: 'name',
		label: 'Paket',
		render: (b) => (
			<>
				<div className="font-medium text-text" title={b.name}>
					{truncateName(b.name)}
				</div>
				{b.description && <div className={`truncate max-w-xs mt-0.5 ${CELL_TEXT_MUTED}`}>{b.description}</div>}
			</>
		),
	},
	{
		key: 'price',
		label: 'Preis',
		align: 'right',
		render: (b) => <span className="font-bold tabular-nums">{formatPrice(b.price)}</span>,
	},
	{
		key: 'items',
		label: 'Positionen',
		align: 'center',
		render: (b) => <span className="text-bgBase-800">{b.items?.length ?? 0}</span>,
	},
	{
		key: 'chevron',
		align: 'right',
		width: 32,
		render: () => <BsChevronRight className="w-3.5 h-3.5 text-bgBase-800" />,
	},
];

// Inline Product Create Card

function ProductCreateInline({ onCancel, onSave, saving }) {
	const { data: fieldDefs = [] } = useVisibleFieldDefs('product');
	const [draft, setDraft] = useState({ customFields: {} });
	const canSubmit = Boolean(draft.name?.trim());

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!canSubmit) return;
		onSave({ ...buildDefaultFieldPatch(fieldDefs, draft), customFields: draft.customFields });
	};

	return (
		<Card className="border-2 border-primary-200 mb-4 ring-2 ring-primary-100">
			<div className="flex items-center gap-2 text-xs font-medium text-primary-700 mb-3">
				<BsPlus className="w-3.5 h-3.5" /> Neues Produkt anlegen
			</div>
			<form onSubmit={handleSubmit} className="space-y-3">
				<CustomFieldsSection
					entityType="product"
					entity={draft}
					values={draft.customFields}
					inline
					showLabel={false}
					onChangeEntity={(fieldKey, value) => setDraft((d) => ({ ...d, [fieldKey]: value }))}
					onChange={(fieldId, value) =>
						setDraft((d) => ({ ...d, customFields: { ...d.customFields, [fieldId]: value } }))
					}
				/>
				<div className="flex justify-end gap-2 pt-1">
					<Button onClick={onCancel} variant="outline" size="sm">
						Abbrechen
					</Button>
					<Button type="submit" disabled={!canSubmit} size="sm" loading={saving}>
						Produkt anlegen
					</Button>
				</div>
			</form>
		</Card>
	);
}

ProductCreateInline.propTypes = {
	onCancel: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	saving: PropTypes.bool,
};

// Bundle Create Inline

function BundleCreateInline({ products, onCancel, onSave, saving }) {
	const [draft, setDraft] = useState({
		name: '',
		price: '',
		description: '',
		items: [{ productId: '', quantity: 1 }],
	});

	const addItem = () => setDraft({ ...draft, items: [...draft.items, { productId: '', quantity: 1 }] });
	const removeItem = (idx) => setDraft({ ...draft, items: draft.items.filter((_, i) => i !== idx) });
	const updateItem = (idx, field, value) => {
		const next = [...draft.items];
		next[idx] = { ...next[idx], [field]: value };
		setDraft({ ...draft, items: next });
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const validItems = draft.items.filter((i) => i.productId);
		if (!draft.name.trim() || validItems.length === 0) return;
		onSave({
			name: draft.name.trim(),
			price: draft.price === '' ? null : Number(draft.price),
			description: draft.description.trim() || null,
			items: validItems.map((i) => ({ productId: i.productId, quantity: Number(i.quantity) || 1 })),
		});
	};

	return (
		<Card className="border-2 border-primary-200 mb-4 ring-2 ring-primary-100">
			<div className="flex items-center gap-2 text-xs font-medium text-primary-700 mb-3">
				<BsPlus className="w-3.5 h-3.5" /> Neues Paket anlegen
			</div>
			<form onSubmit={handleSubmit} className="space-y-3">
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
					<div>
						<label className="block text-xs font-medium text-text mb-1">Name *</label>
						<TextInput
							value={draft.name}
							onChange={(e) => setDraft({ ...draft, name: e.target.value })}
							autoFocus
							required
						/>
					</div>
					<div>
						<label className="block text-xs font-medium text-text mb-1">Preis</label>
						<TextInput
							type="number"
							step="0.01"
							min="0"
							value={draft.price}
							onChange={(e) => setDraft({ ...draft, price: e.target.value })}
							placeholder="0,00"
						/>
					</div>
					<div>
						<label className="block text-xs font-medium text-text mb-1">Beschreibung</label>
						<TextInput
							value={draft.description}
							onChange={(e) => setDraft({ ...draft, description: e.target.value })}
						/>
					</div>
				</div>
				<div>
					<label className="block text-xs font-medium text-text mb-1.5">Produkte *</label>
					<div className="space-y-2">
						{draft.items.map((item, idx) => (
							<div key={idx} className="flex gap-2 items-center">
								<div className="flex-1 min-w-0">
									<Select
										value={item.productId}
										onChange={(e) => updateItem(idx, 'productId', e.target.value)}
									>
										<option value="">Produkt auswählen...</option>
										{products.map((p) => (
											<option key={p.id} value={p.id}>
												{p.articleNumber ? `${p.articleNumber} · ` : ''}
												{p.name}
												{p.price !== null ? ` (${formatPrice(p.price)})` : ''}
											</option>
										))}
									</Select>
								</div>
								<TextInput
									type="number"
									min={1}
									value={item.quantity}
									onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
									style={{ width: 72, flexBasis: 72, maxWidth: 72 }}
									className="flex-none text-center"
								/>
								{draft.items.length > 1 && (
									<IconButton
										icon={BsTrash}
										label="Entfernen"
										tone="danger"
										onClick={() => removeItem(idx)}
										className="flex-none"
									/>
								)}
							</div>
						))}
					</div>
					<button
						type="button"
						onClick={addItem}
						className="mt-2 text-xs text-primary-700 hover:text-primary-700 flex items-center gap-1"
					>
						<BsPlus /> Produkt hinzufügen
					</button>
				</div>
				<div className="flex justify-end gap-2 pt-1">
					<Button onClick={onCancel} variant="outline" size="sm">
						Abbrechen
					</Button>
					<Button
						type="submit"
						disabled={!draft.name.trim() || draft.items.filter((i) => i.productId).length === 0}
						size="sm"
						loading={saving}
					>
						Paket anlegen
					</Button>
				</div>
			</form>
		</Card>
	);
}

BundleCreateInline.propTypes = {
	products: PropTypes.array.isRequired,
	onCancel: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	saving: PropTypes.bool,
};

// Main Page

export default function CrmProductsPage() {
	const navigate = useNavigate();

	const [tab, setTab] = useState('products');
	const [search, setSearch] = useState('');
	const [creating, setCreating] = useState(false);

	const showError = useErrorStore((s) => s.showError);
	const showSuccess = useErrorStore((s) => s.showSuccess);

	const { data: productsData, isLoading: productsLoading } = useProductsList({ search: search || undefined });
	const { data: bundlesData, isLoading: bundlesLoading } = useBundlesList({ search: search || undefined });

	const products = productsData?.products ?? [];
	const bundles = bundlesData?.bundles ?? [];

	const createProduct = useCreateProduct();
	const createBundle = useCreateBundle();

	const handleCreateProduct = async (payload) => {
		try {
			const result = await createProduct.mutateAsync(payload);
			setCreating(false);
			navigate(`/crm/products/${result.id}`);
			showSuccess({ title: 'Produkt erstellt' });
		} catch (err) {
			showError({ error: err, title: 'Fehler beim Erstellen' });
		}
	};

	const handleCreateBundle = async (payload) => {
		try {
			await createBundle.mutateAsync(payload);
			setCreating(false);
			showSuccess({ title: 'Paket erstellt' });
		} catch (err) {
			showError({ error: err, title: 'Fehler beim Erstellen' });
		}
	};

	const isLoading = tab === 'products' ? productsLoading : bundlesLoading;

	return (
		<Page width="full" header={<PageHeader breadcrumbs={[{ label: 'CRM', to: '/crm' }, 'Produkte']} />}>
			<div className="pb-6">
				<div className="mb-5">
					<h1 className="text-lg font-bold text-text">Produkte &amp; Pakete</h1>
					<p className="text-sm text-bgBase-800 mt-1">
						Pflegen Sie Ihren Produktkatalog und verknüpfen Sie Produkte mit Deals und Bestandskunden.
					</p>
				</div>

				<div className="flex items-center justify-between mb-4">
					<Tabs
						tabs={[
							{ id: 'products', label: 'Produkte', icon: BsBox, count: products.length },
							{ id: 'bundles', label: 'Pakete', icon: BsCollection, count: bundles.length },
						]}
						activeId={tab}
						onChange={(nextTab) => {
							setTab(nextTab);
							setCreating(false);
						}}
					/>

					<div className="flex gap-2 items-center">
						<div className="relative w-48">
							<BsSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-bgBase-800 text-xs" />
							<TextInput
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Suchen..."
								className="pr-9"
							/>
						</div>
						<Button size="sm" onClick={() => setCreating(true)}>
							<BsPlus className="text-lg" />
							{tab === 'products' ? 'Neues Produkt' : 'Neues Paket'}
						</Button>
					</div>
				</div>

				{/* Inline create */}
				{creating && tab === 'products' && (
					<ProductCreateInline
						onCancel={() => setCreating(false)}
						onSave={handleCreateProduct}
						saving={createProduct.isPending}
					/>
				)}
				{creating && tab === 'bundles' && (
					<BundleCreateInline
						products={products}
						onCancel={() => setCreating(false)}
						onSave={handleCreateBundle}
						saving={createBundle.isPending}
					/>
				)}

				{tab === 'products' ? (
					<Table
						layoutKey="crmProducts"
						rows={products}
						columns={PRODUCT_COLUMNS}
						isLoading={isLoading}
						onRowClick={(p) => navigate(`/crm/products/${p.id}`)}
						emptyLabel="Noch keine Produkte angelegt"
					/>
				) : (
					<Table
						layoutKey="crmBundles"
						rows={bundles}
						columns={BUNDLE_COLUMNS}
						isLoading={isLoading}
						onRowClick={(b) => navigate(`/crm/products/bundles/${b.id}`)}
						emptyLabel="Noch keine Pakete angelegt"
					/>
				)}
			</div>
		</Page>
	);
}
